// The ProteoRisk API page, in the webserver family's API page layout (base
// URL and ID with copy buttons, a platform toggle, endpoint cards with
// numbered steps, request and response examples). Every endpoint is the
// common XOmics webserver lifecycle under ${WEBSERVER_API_ORIGIN}/api/proteorisk.
import React, { useState } from 'react';

import { XOMICS_WEBSERVER_ORIGIN, serviceApiBase } from '../../shared/xomics-webserver-client';
import { REPORT_PATTERN, SERVICE_CODE } from './api';
import { useProteoRisk } from './context';
import { EXAMPLE_WORKBOOK, ExampleWorkbookLink } from './exampleFiles';
import './proteorisk-api.css';

const PLATFORMS = [
  { key: 'curl', label: 'cURL', icon: 'fas fa-terminal' },
  { key: 'powershell', label: 'PowerShell', icon: 'fab fa-windows' },
  { key: 'python', label: 'Python', icon: 'fab fa-python' },
];

function useCopy() {
  const [copied, setCopied] = useState('');
  const copy = (text, id) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied((current) => (current === id ? '' : current)), 2000);
  };
  return [copied, copy];
}

function CopyButton({ text, id, copied, copy }) {
  return (
    <button type="button" className="PROTEORISK-copy-btn" onClick={() => copy(text, id)} title="Copy to clipboard">
      <i className={copied === id ? 'fas fa-check' : 'fas fa-copy'} aria-hidden="true" />
      {copied === id ? 'Copied' : 'Copy'}
    </button>
  );
}

function CodeBlock({ title, code, id, copied, copy }) {
  return (
    <div className="PROTEORISK-api-code-block">
      <div className="PROTEORISK-api-code-header">
        <span>{title}</span>
        <CopyButton text={code} id={id} copied={copied} copy={copy} />
      </div>
      <pre>{code}</pre>
    </div>
  );
}

function Response({ code, label = 'Response' }) {
  return (
    <div className="PROTEORISK-api-response">
      <span className="PROTEORISK-api-response-label">{label}</span>
      <pre>{code}</pre>
    </div>
  );
}

function Step({ number, title }) {
  return (
    <div className="PROTEORISK-api-step">
      <span className="PROTEORISK-api-step-num">{number}</span>
      <span className="PROTEORISK-api-step-title">{title}</span>
    </div>
  );
}

function Endpoint({ method, title, path, children }) {
  return (
    <div className="PROTEORISK-api-endpoint">
      <div className="PROTEORISK-api-endpoint-header">
        <span className={`PROTEORISK-api-method ${method.toLowerCase()}`}>{method}</span>
        <h3>{title}</h3>
        {path && <code className="PROTEORISK-api-path">{path}</code>}
      </div>
      {children}
    </div>
  );
}

// JSON with one top-level key per line and each value kept on that line.
function compactJson(value) {
  const inline = (item) => JSON.stringify(item, null, 1).replace(/\n\s*/g, ' ');
  return `{\n${Object.entries(value).map(([key, item]) => `  "${key}": ${inline(item)}`).join(',\n')}\n}`;
}

// Request examples for one platform. Values come from the live catalogue when
// it has loaded; otherwise they are named placeholders.
function examples(platform, v) {
  const createBody = compactJson({
    files: [{ position: v.slot, original_name: EXAMPLE_WORKBOOK.filename, declared_bytes: 62130, fills: [`${v.key}:${v.slot}`] }],
    pipelines: [v.pipelineIdValue],
    sample_code: v.sampleCode,
    patient_params: { age: EXAMPLE_WORKBOOK.age, sex: EXAMPLE_WORKBOOK.sex },
    params: { BMI: EXAMPLE_WORKBOOK.bmi },
    display_name: 'Example workbook',
  });
  const assembleBody = '{"parts": [{"part_number": 1, "etag": "ETAG_FROM_STEP_2"}]}';
  if (platform === 'powershell') {
    const h = `-Headers @{ "X-Webserver-Token" = "${v.token}" }`;
    return {
      register: `Invoke-RestMethod -Uri "${v.origin}/auth/register" \`\n  -Method POST -ContentType "application/json" \`\n  -Body '{"token": "${v.token}"}'`,
      catalogue: `Invoke-RestMethod -Uri "${v.base}/catalogue" \`\n  ${h}`,
      create: `$body = @'\n${createBody}\n'@\n$upload = Invoke-RestMethod -Uri "${v.base}/uploads" \`\n  -Method POST -ContentType "application/json" \`\n  ${h} -Body $body\n$upload.uuid`,
      put: `$part = $upload.files[0].parts[0]\n$response = Invoke-WebRequest -Uri $part.put_url -Method PUT \`\n  -InFile "${EXAMPLE_WORKBOOK.filename}"\n$response.Headers.ETag`,
      assemble: `Invoke-RestMethod -Uri "${v.base}/uploads/UPLOAD_UUID/files/${v.slot}/assemble" \`\n  -Method POST -ContentType "application/json" \`\n  ${h} \`\n  -Body '${assembleBody}'`,
      complete: `Invoke-RestMethod -Uri "${v.base}/uploads/UPLOAD_UUID/complete" \`\n  -Method POST -ContentType "application/json" \`\n  ${h} -Body '{}'`,
      status: `Invoke-RestMethod -Uri "${v.base}/uploads/UPLOAD_UUID" \`\n  ${h}`,
      jobs: `Invoke-RestMethod -Uri "${v.base}/jobs?limit=50" \`\n  ${h}`,
      outputs: `Invoke-RestMethod -Uri "${v.base}/uploads/UPLOAD_UUID/jobs/JOB_UUID/outputs" \`\n  ${h}`,
      download: `$file = Invoke-RestMethod \`\n  -Uri "${v.base}/uploads/UPLOAD_UUID/jobs/JOB_UUID/outputs/OUTPUT_ID/download" \`\n  ${h}\nInvoke-WebRequest -Uri $file.downloadUrl -OutFile "report.json"`,
      bundle: `Invoke-RestMethod -Uri "${v.base}/uploads/UPLOAD_UUID/jobs/JOB_UUID/download" \`\n  ${h}`,
    };
  }
  if (platform === 'python') {
    const head = `import requests\n\nBASE = "${v.base}"\nHEADERS = {"X-Webserver-Token": "${v.token}"}\n`;
    return {
      register: `import requests\n\nrequests.post(\n    "${v.origin}/auth/register",\n    json={"token": "${v.token}"},\n).raise_for_status()`,
      catalogue: `${head}\ncatalogue = requests.get(f"{BASE}/catalogue", headers=HEADERS).json()\npipeline = catalogue["pipelines"][0]`,
      create: `${head}\nbody = ${createBody}\nupload = requests.post(f"{BASE}/uploads", json=body, headers=HEADERS).json()\nupload_uuid = upload["uuid"]`,
      put: `parts = []\nwith open("${EXAMPLE_WORKBOOK.filename}", "rb") as handle:\n    for part in upload["files"][0]["parts"]:\n        handle.seek(part["offset"])\n        response = requests.put(part["put_url"], data=handle.read(part["bytes"]))\n        response.raise_for_status()\n        parts.append({"part_number": part["part_number"],\n                      "etag": response.headers["ETag"].strip('"')})`,
      assemble: `requests.post(\n    f"{BASE}/uploads/{upload_uuid}/files/${v.slot}/assemble",\n    json={"parts": parts}, headers=HEADERS,\n).raise_for_status()`,
      complete: `requests.post(\n    f"{BASE}/uploads/{upload_uuid}/complete",\n    json={}, headers=HEADERS,\n).raise_for_status()`,
      status: `detail = requests.get(f"{BASE}/uploads/{upload_uuid}", headers=HEADERS).json()\njob = detail["jobs"][0]\nprint(job["state"], job["run_status"])`,
      jobs: `jobs = requests.get(f"{BASE}/jobs", params={"limit": 50}, headers=HEADERS).json()`,
      outputs: `outputs = requests.get(\n    f"{BASE}/uploads/{upload_uuid}/jobs/{job['uuid']}/outputs",\n    headers=HEADERS,\n).json()["outputs"]\nreport = next(o for o in outputs if o["file_pattern"] == "\${JOB}_olink_report.json")`,
      download: `signed = requests.get(\n    f"{BASE}/uploads/{upload_uuid}/jobs/{job['uuid']}/outputs/{report['id']}/download",\n    headers=HEADERS,\n).json()\nresult = requests.get(signed["downloadUrl"]).json()`,
      bundle: `bundle = requests.get(\n    f"{BASE}/uploads/{upload_uuid}/jobs/{job['uuid']}/download",\n    headers=HEADERS,\n).json()["downloadUrl"]`,
    };
  }
  const h = `-H "X-Webserver-Token: ${v.token}"`;
  return {
    register: `curl -X POST "${v.origin}/auth/register" \\\n  -H "Content-Type: application/json" \\\n  -d '{"token": "${v.token}"}'`,
    catalogue: `curl "${v.base}/catalogue" \\\n  ${h}`,
    create: `curl -X POST "${v.base}/uploads" \\\n  ${h} \\\n  -H "Content-Type: application/json" \\\n  -d '${createBody}'`,
    put: `# One PUT per entry in files[0].parts; send bytes offset to offset + bytes - 1.\n# A single workbook normally fits in one part.\ncurl -X PUT "PUT_URL_FROM_STEP_1" \\\n  --data-binary @${EXAMPLE_WORKBOOK.filename} -D - -o /dev/null | grep -i etag`,
    assemble: `curl -X POST "${v.base}/uploads/UPLOAD_UUID/files/${v.slot}/assemble" \\\n  ${h} \\\n  -H "Content-Type: application/json" \\\n  -d '${assembleBody}'`,
    complete: `curl -X POST "${v.base}/uploads/UPLOAD_UUID/complete" \\\n  ${h} \\\n  -H "Content-Type: application/json" -d '{}'`,
    status: `curl "${v.base}/uploads/UPLOAD_UUID" \\\n  ${h}`,
    jobs: `curl "${v.base}/jobs?limit=50" \\\n  ${h}`,
    outputs: `curl "${v.base}/uploads/UPLOAD_UUID/jobs/JOB_UUID/outputs" \\\n  ${h}`,
    download: `curl "${v.base}/uploads/UPLOAD_UUID/jobs/JOB_UUID/outputs/OUTPUT_ID/download" \\\n  ${h}\n# then fetch the short-lived link it returns:\ncurl -o report.json "DOWNLOAD_URL"`,
    bundle: `curl "${v.base}/uploads/UPLOAD_UUID/jobs/JOB_UUID/download" \\\n  ${h}`,
  };
}

const PARAMETERS = [
  ['patient_params.age', 'integer', 'Yes', 'Age in whole years, 1 to 129.'],
  ['patient_params.sex', 'string', 'Yes', 'Male or Female.'],
  ['params.BMI', 'number', 'No', '5 to 100. Leave it out when unknown; it is never filled in for you.'],
  ['display_name', 'string', 'No', 'A name for the analysis, shown in your list (up to 255 characters).'],
  ['metadata', 'object', 'No', 'Your own presentation fields, up to 64 KiB.'],
];

const ERRORS = [
  ['400', 'Invalid input, for example PATIENT_FIELDS_MISSING when age or sex is missing, or an invalid upload id.'],
  ['401', 'WEBSERVER_AUTH_REQUIRED or WEBSERVER_CREDENTIAL_INVALID: the ID is missing, malformed or not registered.'],
  ['403', 'WEBSERVER_CREDENTIAL_DISABLED: the ID has been switched off.'],
  ['404', 'SUBMISSION_NOT_FOUND: the analysis does not belong to this ID.'],
  ['409', 'PIPELINE_NOT_AVAILABLE or SAMPLE_CODE_NOT_AVAILABLE: copy the values from /catalogue again.'],
  ['413', 'The metadata object is larger than 64 KiB.'],
  ['502 / 503', 'The analysis service could not be reached. Try again shortly.'],
];

export default function ApiGuide() {
  const { cookieId, contract, contractError } = useProteoRisk();
  const [platform, setPlatform] = useState('curl');
  const [copied, copy] = useCopy();
  const base = serviceApiBase(SERVICE_CODE);
  const pipeline = contract?.pipeline;
  const values = {
    origin: XOMICS_WEBSERVER_ORIGIN,
    base,
    token: cookieId || 'YOUR-ID',
    pipelineIdValue: pipeline ? Number(pipeline.id) : 'PIPELINE_ID',
    key: pipeline ? (pipeline.key || (pipeline.mode ? `${pipeline.name}:${pipeline.mode}` : pipeline.name)) : 'PIPELINE_KEY',
    slot: contract ? contract.workbookSlot : 1,
    sampleCode: contract ? contract.sampleCodes[0] : 'SAMPLE_CODE',
  };
  const code = examples(platform, values);
  const block = (title, key) => (
    <CodeBlock title={title} code={code[key]} id={`${platform}-${key}`} copied={copied} copy={copy} />
  );

  return (
    <div className="PROTEORISK-api-container">
      <div className="PROTEORISK-api-content">
        <div className="PROTEORISK-api-header">
          <h1>API Documentation</h1>
          <p>Run ProteoRisk from your own scripts. It uses the same upload, job and result steps as the page.</p>
        </div>

        {contractError && <div className="PROTEORISK-api-warning">{contractError}</div>}

        <div className="PROTEORISK-api-section">
          <div className="PROTEORISK-api-base-info">
            <div className="PROTEORISK-api-info-row">
              <span className="PROTEORISK-api-label">Base URL</span>
              <code className="PROTEORISK-api-value">{base}</code>
              <CopyButton text={base} id="base" copied={copied} copy={copy} />
            </div>
            <div className="PROTEORISK-api-info-row">
              <span className="PROTEORISK-api-label">Your ID</span>
              <code className="PROTEORISK-api-value plain">{cookieId || 'Loading...'}</code>
              {cookieId && <CopyButton text={cookieId} id="token" copied={copied} copy={copy} />}
            </div>
            <div className="PROTEORISK-api-info-row">
              <span className="PROTEORISK-api-label">Pipeline</span>
              <code className="PROTEORISK-api-value plain">
                {pipeline ? `${pipeline.display_name || pipeline.name} ${pipeline.code_version || ''} (id ${pipeline.id})` : 'Loading...'}
              </code>
            </div>
          </div>
          <p className="PROTEORISK-api-note">
            Send your ID in the <code>X-Webserver-Token</code> header on every request. Analyses are only visible to
            the ID that created them. Always take the pipeline id, pipeline key, sample code and workbook slot from
            {' '}<code>/catalogue</code>; the examples below already use the live values.
          </p>
        </div>

        <div className="PROTEORISK-api-platform-toggle" role="tablist" aria-label="Example language">
          {PLATFORMS.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={platform === item.key}
              className={`PROTEORISK-platform-btn ${platform === item.key ? 'active' : ''}`}
              onClick={() => setPlatform(item.key)}
            >
              <i className={item.icon} aria-hidden="true" /> {item.label}
            </button>
          ))}
        </div>

        <div className="PROTEORISK-api-notice">
          <i className="fas fa-info-circle" aria-hidden="true" />
          <span>
            Every analysis needs the age in whole years and the sex; BMI is optional. Uploads are processed without
            a patient record.
            {' '}<ExampleWorkbookLink>Example workbook</ExampleWorkbookLink>
          </span>
        </div>

        <Endpoint method="POST" title="Register your ID" path="/auth/register">
          <p className="PROTEORISK-api-text">
            Once per ID, before the first request. The ID is a secret you choose: 8 to 256 letters, digits or
            {' '}<code>. _ ~ -</code>, for example a UUID. The backend keeps only its hash. Send it later as
            {' '}<code>X-Webserver-Token</code> or as <code>Authorization: Bearer</code>.
          </p>
          {block('/auth/register', 'register')}
          <Response label="Response (201)" code={'{ "credentialId": "5f0c...", "createdAt": 1790000000000 }'} />
        </Endpoint>

        <Endpoint method="GET" title="Read the catalogue" path="/catalogue">
          <p className="PROTEORISK-api-text">The active ProteoRisk pipeline with its sample types and its workbook slot.</p>
          {block('/catalogue', 'catalogue')}
          <Response code={`{
  "service": { "code": "proteorisk", "displayName": "ProteoRisk" },
  "pipelines": [{
    "id": ${JSON.stringify(values.pipelineIdValue)},
    "key": ${JSON.stringify(values.key)},
    "display_name": ${JSON.stringify(pipeline?.display_name || 'Olink Proteomics')},
    "code_version": ${JSON.stringify(pipeline?.code_version || '2.0.0')},
    "sample_codes": [${JSON.stringify(values.sampleCode)}],
    "inputs": [{ "position": ${values.slot}, "title": "Olink proteomics workbook", "file_format": "xlsx" }]
  }]
}`}
          />
        </Endpoint>

        <Endpoint method="POST" title="Submit a workbook" path="/uploads">
          <Step number={1} title="Create the upload with the details" />
          {block('POST /uploads', 'create')}
          <Response code={`{
  "uuid": "UPLOAD_UUID",
  "files": [{
    "position": ${values.slot},
    "parts": [{ "part_number": 1, "offset": 0, "bytes": 62130, "put_url": "https://..." }]
  }]
}`}
          />
          <Step number={2} title="Upload the workbook to the signed part URL and keep the ETag" />
          {block('PUT put_url', 'put')}
          <Step number={3} title="Assemble the file from its parts" />
          {block(`POST /uploads/:uuid/files/${values.slot}/assemble`, 'assemble')}
          <Step number={4} title="Start the analysis" />
          {block('POST /uploads/:uuid/complete', 'complete')}
          <Response code={'{ "status": "ready", "jobs": [{ "uuid": "JOB_UUID", "state": "pending" }] }'} />
          <table className="PROTEORISK-api-table">
            <thead>
              <tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr>
            </thead>
            <tbody>
              {PARAMETERS.map(([field, type, required, description]) => (
                <tr key={field}>
                  <td><code>{field}</code></td>
                  <td>{type}</td>
                  <td>{required}</td>
                  <td>{description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Endpoint>

        <Endpoint method="GET" title="Check the status" path="/uploads/:uuid">
          {block('GET /uploads/:uuid', 'status')}
          <Response code={`{
  "upload": { "uuid": "UPLOAD_UUID", "status": "complete", "created_at": 1790000000000 },
  "jobs": [{
    "uuid": "JOB_UUID",
    "state": "complete",
    "run_status": "success",
    "params": { "AGE": 58, "SEX": "Male", "BMI": 29.4 },
    "error_message": null
  }]
}`}
          />
          <div className="PROTEORISK-api-statuses">
            <span className="PROTEORISK-api-status-label">run_status:</span>
            {['queued', 'assigned', 'running', 'success', 'failed'].map((status) => (
              <span key={status} className={`PROTEORISK-api-status ${status}`}>{status}</span>
            ))}
          </div>
          <Step number={2} title="Or list every analysis of your ID" />
          {block('GET /jobs', 'jobs')}
        </Endpoint>

        <Endpoint method="GET" title="List the result files" path="/uploads/:uuid/jobs/:jobUuid/outputs">
          {block('GET /outputs', 'outputs')}
          <Response code={`{
  "outputs": [
    { "id": 101, "filename": "JOB_olink_report.json", "file_pattern": "\${JOB}_olink_report.json" },
    { "id": 103, "filename": "new_schema/JOB_olink_report_summary.xlsx", "file_pattern": "new_schema/\${JOB}_olink_report_summary.xlsx" }
  ]
}`}
          />
        </Endpoint>

        <Endpoint method="GET" title="Download a result" path="/uploads/:uuid/jobs/:jobUuid/outputs/:outputId/download">
          {block('GET /outputs/:outputId/download', 'download')}
          <Response code={'{ "outputId": 101, "filename": "JOB_olink_report.json", "downloadUrl": "https://...", "expiresAt": 1790000600000 }'} />
          <Step number={2} title="Or download every result file as one bundle" />
          {block('GET /uploads/:uuid/jobs/:jobUuid/download', 'bundle')}
          <div className="PROTEORISK-api-file-info">
            <i className="fas fa-file-code" aria-hidden="true" />
            <span>
              <code>{REPORT_PATTERN}</code> holds every result. Scores run from 0 to 1 (the page shows them
              from 0 to 100) and tiers are low, moderate or high.
            </span>
          </div>
          <Response
            label="Report file (shortened)"
            code={`{
  "format": "patient_report_pipeline_v1",
  "current_findings": {
    "Circulatory system": [{
      "disease": "Hypertension (High Blood Pressure)",
      "score": 0.8435,
      "tier": "high",
      "interpretation": "...",
      "protein_panel": [{ "gene_name": "ACE", "protein_id": "P12821", "z": 1.84, "flagged": true }]
    }]
  },
  "ten_year_risk": { "...": [] },
  "provenance": {
    "observed_protein_count": 2765,
    "observed_coverage_fraction": 0.947,
    "panel_mapping": { "external_panel_size": 2919, "matched_count": 2765 }
  }
}`}
          />
        </Endpoint>

        <div className="PROTEORISK-api-section">
          <h2 className="PROTEORISK-api-section-title">Error codes</h2>
          <table className="PROTEORISK-api-table">
            <thead><tr><th>Code</th><th>Meaning</th></tr></thead>
            <tbody>
              {ERRORS.map(([status, meaning]) => (
                <tr key={status}><td><span className="PROTEORISK-api-status-code">{status}</span></td><td>{meaning}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="PROTEORISK-api-support">
          <div className="PROTEORISK-api-support-icon"><i className="fas fa-life-ring" aria-hidden="true" /></div>
          <div>
            <h3>Need help with the API?</h3>
            <p>For access questions or integration help, write to our team.</p>
            <a href="mailto:hello@phenomelongevity.com" className="PROTEORISK-api-support-email">
              <i className="fas fa-envelope" aria-hidden="true" /> hello@phenomelongevity.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
